import './App.css';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import GameInterface from './components/GameInterface';
import AdminDashboard from './components/AdminDashboard';
import LiveSchedule from './components/LiveSchedule';

function JoinPage() {
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const validateUsername = (value: string): string | null => {
    const trimmed = value.trim();

    if (!trimmed) {
      return 'Username is required';
    }

    if (trimmed.length < 3) {
      return 'Username must be at least 3 characters long';
    }

    if (trimmed.length > 20) {
      return 'Username cannot exceed 20 characters';
    }

    // Allowed: letters, numbers, underscore, hyphen, period
    const validPattern = /^[a-zA-Z][a-zA-Z0-9_.-]*$/;
    if (!validPattern.test(trimmed)) {
      return 'Username can only contain letters, numbers, underscores, hyphens, or periods. Must start with a letter.';
    }

    // Optional: block reserved names
    const reserved = ['admin', 'host', 'system', 'bot', 'server'];
    if (reserved.includes(trimmed.toLowerCase())) {
      return 'This username is reserved. Please choose another.';
    }

    return null; // valid
  };

  const handleJoin = (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateUsername(username);

    if (validationError) {
      setError(validationError);
      return;
    }

    // Clear error and proceed
    setError(null);
    navigate(`/play/${encodeURIComponent(username.trim())}`);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setUsername(newValue);
    // Real-time validation (optional)
    setError(validateUsername(newValue));
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px 0',
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
        color: '#ecf0f1',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '30px 20px',
          background: 'rgba(255,255,255,0.08)',
          borderRadius: '20px',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(255,255,255,0.15)',
          boxShadow: '0 15px 40px rgba(0,0,0,0.5)',
          width: '90%',
          maxWidth: '450px',
          textAlign: 'center',
          marginTop: 'auto',
          marginBottom: 'auto',
        }}
      >
        <img
          src="/logo_custom.png"
          alt="Crack It Quizzes Logo"
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            marginBottom: '10px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
            border: '3px solid rgba(255,255,255,0.2)'
          }}
        />
        <h1 style={{ fontSize: '2rem', marginBottom: '10px' }}>Welcome to Crack It Quizzes</h1>
        <p style={{ fontSize: '1.1rem', marginBottom: '20px', color: '#bdc3c7' }}>
          Choose your username to join
        </p>

        <form onSubmit={handleJoin}>
          <input
            type="text"
            value={username}
            onChange={handleChange}
            placeholder="Enter your username..."
            maxLength={20}
            autoFocus
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '14px',
              fontSize: '1.2rem',
              borderRadius: '12px',
              border: error ? '2px solid #e74c3c' : '2px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.1)',
              color: '#ecf0f1',
              marginBottom: '10px',
              outline: 'none',
              textAlign: 'center',
              transition: 'border 0.3s',
            }}
          />

          {error && (
            <p style={{ color: '#e74c3c', fontSize: '1rem', marginBottom: '15px' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={!username.trim() || !!error}
            style={{
              padding: '12px 40px',
              fontSize: '1.3rem',
              fontWeight: 'bold',
              background: username.trim() && !error ? '#3498db' : '#7f8c8d',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: username.trim() && !error ? 'pointer' : 'not-allowed',
              transition: 'background 0.3s',
              opacity: username.trim() && !error ? 1 : 0.6,
            }}
          >
            Join Game
          </button>
        </form>

        <p style={{ marginTop: '15px', fontSize: '0.9rem', color: '#95a5a6' }}>
          Username must be 3–20 chars, start with a letter, and can contain letters, numbers, _, -, .
        </p>
      </div>
      {/* Schedule added below the join card on the home page */}
      <div style={{ padding: '0 10px', width: '100%', maxWidth: '1350px', marginTop: '15px', marginBottom: '10px' }}>
        <LiveSchedule />
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Username selection */}
        <Route path="/" element={<JoinPage />} />

        {/* Player game view with dynamic username */}
        <Route path="/play/:username" element={<PlayerWrapper />} />

        {/* Admin dashboard */}
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    </Router>
  );
}

// Wrapper to get username from URL params
function PlayerWrapper() {
  const { username } = useParams<{ username: string }>();
  return <GameInterface username={decodeURIComponent(username || 'Guest')} />;
}

export default App;